import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from 'src/shared/lib/useQuery';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { AuthLoading } from 'src/features/auth/contexts/AuthLoading';
import { toTitleCase } from 'src/shared/lib/textFormat.utils';
import { Organisation, Role } from 'src/shared/types/organisation.types';
import { ReactChildren } from 'src/shared/types/util.types';

function createOrgFetch(apiFetch: OrgFetch, orgId: string) {
  return async function orgFetch<T>(path: string, init: RequestInit = {}, skipDefaultHeaders = false) {
    if (!orgId) {
      return new Promise<never>(() => {});
    }
    const [pathname, params] = path.split('?');
    const searchParams = new URLSearchParams(params);
    searchParams.set('organisation', orgId);
    return await apiFetch<T>(
      `${pathname}?${searchParams.toString()}`,
      {
        method: 'GET',
        ...init,
      },
      skipDefaultHeaders
    );
  };
}

const TIMEOUT_DELAY = 300;
export const ROLES: readonly Role[] = [
  'ADMIN',
  'CONTRIBUTOR',
  'READER',
  'SUPPORT',
  'SALES',
  'OUTLOOK_APP_ONLY',
] as const;
export type OrgFetch = <T>(path: string, init?: RequestInit, skipDefaultHeaders?: boolean) => Promise<FetchResponse<T>>;

export interface FetchResponse<T> extends Response {
  json: () => Promise<T>;
}

export interface OrgProviderProps extends ReactChildren {
  loadingFrom?: number;
  loadingTo?: number;
  disableOrgBadge?: boolean;
}

export interface useOrgProps {
  role: Role;
  isSupport?: boolean;
  isLoading: boolean;
  currentOrg: Organisation;
  featureFlags: Set<string> | null;
  orgFetch: OrgFetch;
  switchOrg: (orgId: string, skipRedirect?: boolean) => void;
}

const OrgContext = createContext<useOrgProps>({
  role: 'READER',
  isSupport: false,
  isLoading: true,
  featureFlags: null,
  currentOrg: {} as Organisation,
  orgFetch: () => {
    throw new Error('orgFetch accessed before initialization');
  },
  switchOrg: () => {
    throw new Error('switchOrg accessed before initialization');
  },
});

export function useOrg() {
  return useContext(OrgContext);
}

export function OrgProvider({
  children,
  loadingFrom = 70,
  loadingTo = 100,
  disableOrgBadge = false,
}: OrgProviderProps) {
  const navigate = useNavigate();
  const { apiFetch, logout, user, isLoading: isAuthLoading } = useAuth();
  const [isSupport, setIsSupport] = useState(false);
  const [timeoutLoading, setTimeoutLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState<Set<string> | null>(null);
  const [organisationId, setOrganisationId] = useState<string | undefined>();
  const orgMountTime = useRef(Date.now());

  const orgLog = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.info(message, {
      ...context,
      source: 'OrgContext',
      elapsedMs: Date.now() - orgMountTime.current,
    });
  };

  const orgLogError = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.error(message, {
      ...context,
      source: 'OrgContext',
      elapsedMs: Date.now() - orgMountTime.current,
    });
  };

  orgLog('[mount] OrgProvider mounting');

  useEffect(() => {
    const timer = setTimeout(() => {
      orgLog('[timeout] loading timeout elapsed');
      setTimeoutLoading(false);
    }, TIMEOUT_DELAY);

    return () => clearTimeout(timer);
  }, []);

  function getFeatureFlags(orgId: string) {
    apiFetch<string[]>(`/api/v2/organisations/${orgId}/featureflags`, {})
      .then((res) => res.json())
      .then((data) => setFeatureFlags(new Set(data)));
  }

  function initTrackers(org: Organisation, role: Role) {
    datadogRum.setGlobalContextProperty('organisation', {
      id: org.id,
      name: org.name,
      orgType: org.orgType,
    });
    datadogRum.setUserProperty('role', role);
  }

  const orgQuery = useQuery({
    queryKey: ['current-org'],
    queryFn: async () => {
      orgLog('[queryFn] fetching current org', { organisationId: organisationId ?? 'none' });
      let res: FetchResponse<Organisation>;
      if (!organisationId) {
        res = await apiFetch<Organisation>('/api/v2/organisations/current');
      } else {
        res = await apiFetch<Organisation>(`/api/v2/organisations/current/${organisationId}`, {
          method: 'POST',
        });
      }

      const org = await res.json();
      if (!res.ok) {
        orgLogError('[queryFn] failed to get current org', { status: res.status, orgId: (org as any)?.id });
        throw new Error('Failed to get current org');
      }

      orgLog('[queryFn] org fetched successfully', { orgId: org.id, orgName: org.name });

      getFeatureFlags(org.id);
      setOrganisationId(org.id);

      orgLog('[queryFn] fetching role', { orgId: org.id });
      const roleRes = await apiFetch<Role>(`/api/v2/organisations/${org.id}/role`);
      const role = await roleRes.json();
      if (!res.ok || !ROLES.includes(role)) {
        orgLogError('[queryFn] failed to fetch role', { roleStatus: roleRes.status, role: String(role) });
        throw new Error('failed to fetch role');
      }
      orgLog('[queryFn] role fetched', { role, orgId: org.id });

      if (role === 'SUPPORT') {
        setIsSupport(true);
      }

      initTrackers(org, role);

      return { org, role };
    },
    enabled: !isAuthLoading,
    staleTime: 0,
    retry: 2,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (orgQuery.isError) {
      orgLogError('[orgError] orgQuery error, triggering logout', { error: String(orgQuery.error) });
      logout();
    }
  }, [orgQuery.isError]);

  useEffect(() => {
    if (!orgQuery.data) {
      return;
    }
    if (organisationId !== orgQuery.data.org.id) {
      orgQuery.refetch();
    }
  }, [organisationId]);

  useEffect(() => {
    if (orgQuery.data?.org?.id && orgQuery.data?.role) {
      getFeatureFlags(orgQuery.data.org.id);
    }
  }, [orgQuery.data?.org?.id, orgQuery.data?.role]);

  const switchOrg = useCallback(
    async (orgId: string, skipRedirect = false) => {
      setOrganisationId(orgId);
      if (!skipRedirect) {
        navigate({ to: '/' });
      }
    },
    [navigate]
  );

  const orgFetch = useMemo(
    () => createOrgFetch(apiFetch, orgQuery.data?.org?.id ?? ''),
    [apiFetch, orgQuery.data?.org?.id]
  );

  const memoCurrentOrg = useMemo(() => {
    if (!orgQuery.data?.org) {
      return {} as Organisation;
    }
    const org = structuredClone(orgQuery.data.org);
    const userRole = isSupport ? user?.settings?.assumeRole?.[org.id] || orgQuery.data.role : orgQuery.data.role;
    if (userRole === 'SALES') {
      org.name = toTitleCase(`${org.industry?.[0] || 'Acme'} company`) || 'Acme company';
      org.subdomain = `${org.industry?.[0] || 'acme'}.tekst.com`;
    }
    return org;
  }, [orgQuery.data?.org, orgQuery.data?.role, isSupport, user?.settings?.assumeRole]);

  const memoRole: Role = useMemo(() => {
    return (
      (orgQuery.data?.org?.id && (user?.settings?.assumeRole?.[orgQuery.data.org.id] as Role | undefined)) ||
      (orgQuery.data?.role as Role | undefined) ||
      'READER'
    );
  }, [orgQuery.data?.org?.id, orgQuery.data?.role, user?.settings?.assumeRole]);

  // Block page content during org switch to prevent stale data from the previous org leaking through.
  // orgQuery.isLoading is only true on the FIRST fetch (no cached data).
  // During org switching (refetch), we detect the mismatch between the requested org and the loaded org.
  const isSwitchingOrg =
    organisationId !== undefined && orgQuery.data !== undefined && organisationId !== orgQuery.data.org.id;
  const isInitialLoad = orgQuery.isLoading;
  const isLoading = isInitialLoad || isSwitchingOrg || !orgQuery.data;

  const value: useOrgProps = useMemo(
    () => ({
      isSupport,
      isLoading,
      featureFlags,
      currentOrg: memoCurrentOrg,
      role: memoRole,
      orgFetch,
      switchOrg,
    }),
    [isSupport, isLoading, featureFlags, memoCurrentOrg, memoRole, orgFetch, switchOrg]
  );

  orgLog('[render] OrgProvider rendering', {
    hasCurrentOrg: !!value.currentOrg && !!value.currentOrg.id,
    orgId: value.currentOrg?.id ?? 'undefined',
    isLoading,
    isError: orgQuery.isError,
  });

  return (
    <OrgContext.Provider value={value}>
      {!value.currentOrg || orgQuery.isLoading || timeoutLoading ? (
        <div data-testid="org-loading">
          <AuthLoading text="Connecting to org" delay={TIMEOUT_DELAY} from={loadingFrom} to={loadingTo} />
        </div>
      ) : (
        <>
          {children}
          {!disableOrgBadge && value.currentOrg.orgType === 'TEST' && <OrgBadge orgType={'Test'} />}
          {!disableOrgBadge && value.currentOrg.orgType === 'INTERNAL' && <OrgBadge orgType={'Internal'} />}
        </>
      )}
    </OrgContext.Provider>
  );
}

function OrgBadge({ orgType }: { orgType: string }) {
  return (
    <div className="absolute bottom-0 right-0 bg-primary text-white py-2 px-3 text-center font-bold z-50">
      {orgType} Organisation
    </div>
  );
}
